import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Star, User, Building2, ListChecks, Plus, Zap } from 'lucide-react';
import { favoritesApi, listsApi } from '../../lib/api';
import { effectiveFirstName, effectiveLastName } from '../../lib/personPrecedence';
import type { List, ListItemView } from '../../types/list';

interface FavChip { key: string; to: string; name: string; isPerson: boolean }

function toChip(item: ListItemView): FavChip | null {
  if (item.person) {
    const name = `${effectiveFirstName(item.person)} ${effectiveLastName(item.person)}`.trim();
    return { key: `p-${item.person.id}`, to: `/people/${item.person.id}`, name: name || 'Unknown', isPerson: true };
  }
  if (item.organization) {
    return { key: `o-${item.organization.id}`, to: `/organizations/${item.organization.id}`, name: item.organization.name, isPerson: false };
  }
  return null; // skip programs — ambiguous as a chip
}

/**
 * Quick access for the Performance tab — favorited people/schools as chips and
 * the rep's saved lists as pills. Actionable shortcuts that replace the old
 * low-signal "recently viewed" columns. Read-only fetch via existing APIs.
 */
export function QuickAccess() {
  const [favs, setFavs] = useState<FavChip[]>([]);
  const [lists, setLists] = useState<List[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [favList, listsRes] = await Promise.all([
          favoritesApi.get().catch(() => null),
          listsApi.list('mine').catch(() => ({ lists: [] as List[] })),
        ]);
        let favChips: FavChip[] = [];
        if (favList && (favList.list.count ?? 0) > 0) {
          const { items } = await listsApi.items(favList.list.id, { limit: 10 });
          favChips = items.map(toChip).filter((c): c is FavChip => c !== null).slice(0, 8);
        }
        const myLists = (listsRes.lists || []).filter((l) => !l.isSystem && l.isActive).slice(0, 6);
        if (!cancelled) { setFavs(favChips); setLists(myLists); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="card p-4 animate-pulse">
        <div className="h-4 w-24 bg-gray-100 dark:bg-gray-800 rounded mb-3" />
        <div className="flex gap-2">{[1, 2, 3].map((i) => <div key={i} className="h-8 w-24 bg-gray-100 dark:bg-gray-800 rounded-full" />)}</div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Favorites */}
      <div>
        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
          <Star className="h-3.5 w-3.5 text-amber-500 fill-current" /> Favorites
        </h3>
        {favs.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500">Star a school or coach to pin it here.</p>
        ) : (
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {favs.map((f) => {
              const Icon = f.isPerson ? User : Building2;
              return (
                <Link
                  key={f.key}
                  to={f.to}
                  title={f.name}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 hover:border-blast-400 dark:hover:border-blast-500 transition-colors"
                >
                  <Icon className="h-3.5 w-3.5 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                  <span className="max-w-[10rem] truncate">{f.name}</span>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* My lists */}
      <div>
        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
          <ListChecks className="h-3.5 w-3.5" /> My lists
        </h3>
        <div className="flex flex-wrap gap-2">
          {lists.map((l) => (
            <Link
              key={l.id}
              to={`/lists/${l.id}`}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm hover:border-blast-400 dark:hover:border-blast-500 transition-colors"
            >
              <span
                className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${l.color ? '' : 'bg-gray-300 dark:bg-gray-600'}`}
                style={l.color ? { backgroundColor: l.color } : undefined}
              />
              <span className="text-gray-700 dark:text-gray-200 max-w-[10rem] truncate">{l.name}</span>
              {l.kind === 'SMART' && <Zap className="h-3 w-3 text-blast-500 dark:text-blast-400 flex-shrink-0" />}
              {typeof l.count === 'number' && (
                <span className="text-xs tabular-nums text-gray-400 dark:text-gray-500">{l.count}</span>
              )}
            </Link>
          ))}
          <Link
            to="/lists"
            className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400 hover:border-blast-400 hover:text-blast-600 dark:hover:text-blast-300 transition-colors"
          >
            <Plus className="h-3.5 w-3.5" /> New list
          </Link>
        </div>
      </div>
    </div>
  );
}
